import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LandingNav } from '../components/Nav';
import SiteFooter from '../components/SiteFooter';

const TOKENS = {
  /** Same as homepage `#features` — FeaturesSection `rgb(255, 252, 249)` */
  bg: '#fffcf9',
  /** Same as homepage `#woman-chapters` — ChaptersSection `rgb(249, 246, 241)` */
  blade2: 'rgb(249, 246, 241)',
  text: '#111',
  body: '#7a6b63',
  pink: '#d4537e',
  pinkL: '#fbeaf0',
  pinkM: '#f4c0d1',
  pinkD: '#993556',
  pinkDk: '#72243e',
  sageL: '#e1f5ee',
  rule: '#e8e0d8',
};

/** Avoid apex → www POST redirect (breaks browser fetch). Post directly to www on production. */
function blogNewsletterSubscribeUrl() {
  if (typeof window === 'undefined') return '/api/blog-newsletter-subscribe';
  const { protocol, hostname } = window.location;
  if (hostname === 'orivonai.com') {
    return `${protocol}//www.orivonai.com/api/blog-newsletter-subscribe`;
  }
  return '/api/blog-newsletter-subscribe';
}

const READING_CARDS = [
  {
    source: 'The New Yorker',
    title: 'Thanksgiving in Mongolia',
    author: 'By Ariel Levy',
    why: "Ariel Levy's National Magazine Award–winning essay on late-term miscarriage. The piece every conversation about pregnancy loss eventually returns to.",
    category: 'Loss',
    meta: 'Essay · 30 min',
    href: 'https://www.newyorker.com/magazine/2013/11/18/thanksgiving-in-mongolia',
  },
  {
    source: "Harper's Magazine",
    title: 'The Art of Waiting',
    author: 'By Belle Boggs',
    why: 'Belle Boggs on infertility, IVF, and what it means to wait for a body to do something it might never do.',
    category: 'Trying to Conceive',
    meta: 'Essay · 25 min',
    href: 'https://harpers.org/archive/2012/05/the-art-of-waiting/',
  },
  {
    source: 'Cup of Jo',
    title: "What If Trying for a Baby Isn't Working?",
    author: 'By Taylor Hahn',
    why: 'Taylor Hahn writes from the middle of IVF — not after the success story, but inside the unknowing.',
    category: 'IVF',
    meta: 'Essay · 12 min',
    href: 'https://cupofjo.com/2022/05/02/infertility-essay/',
  },
  {
    source: 'Cup of Jo',
    title: 'How I Survived Postpartum Psychosis',
    author: 'By Jessica Ekhoff',
    why: 'Jessica Ekhoff on the maternal mental health crisis no one warned her about, and how she found her way back.',
    category: 'Postpartum',
    meta: 'Essay · 15 min',
    href: 'https://cupofjo.com/2023/07/24/how-i-survived-postpartum-psychosis/',
  },
  {
    source: 'ReproductiveFacts.org',
    title: 'In Vitro Fertilization Treatment Journey',
    author: 'By the American Society for Reproductive Medicine',
    why: "A clear, clinically authoritative walkthrough of the IVF process from the American Society for Reproductive Medicine and SART — the field's two governing bodies. The piece every patient deserves before their first appointment.",
    category: 'Fertility',
    meta: 'Clinical Guide · 20 min',
    href: 'https://www.reproductivefacts.org/patient-journeys/in-vitro-fertilization-treatment/',
  },
  {
    source: 'The New Yorker',
    title: 'Can Motherhood Be a Mode of Rebellion?',
    author: 'By Jia Tolentino',
    why: 'Jia Tolentino on identity, expectation, and the stories we tell about mothers — sharp, personal, and impossible to skim.',
    category: 'Motherhood',
    meta: 'Essay · 18 min',
    href: 'https://www.newyorker.com/books/under-review/can-motherhood-be-a-mode-of-rebellion',
  },
];

const BLOG_PAGE_CSS = `
.blog-page { background: ${TOKENS.bg}; min-height: 100vh; color: ${TOKENS.text}; }
.blog-page .blog-hero-band {
  width: 100%;
  background: ${TOKENS.bg};
  border-bottom: 1px solid ${TOKENS.rule};
  box-sizing: border-box;
}
.blog-page .blog-hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  min-height: 600px;
  max-height: 600px;
  align-items: center;
  padding: 0 56px;
  max-width: 1320px;
  margin: 0 auto;
  box-sizing: border-box;
}
.blog-page .blog-hero__text-col {
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 480px;
  margin-left: auto;
  height: 100%;
}
.blog-page .blog-hero__image-col {
  background: transparent;
  padding: 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
}
.blog-page .blog-hero__image-wrapper {
  position: relative;
  width: 100%;
  max-width: 420px;
  aspect-ratio: 4 / 5;
  height: auto;
  max-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.blog-page .blog-hero__image {
  width: 100%;
  height: 100%;
  max-height: 520px;
  max-width: 100%;
  object-fit: cover;
  object-position: 50% 22%;
  border-radius: 4px;
  box-shadow: 0 24px 48px -24px rgba(114, 36, 62, 0.18);
  display: block;
}
.blog-page .blog-hero__avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(114, 36, 62, 0.1);
  flex-shrink: 0;
}
.blog-page .blog-hero__stamp {
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 1;
  background: ${TOKENS.bg};
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${TOKENS.pinkD};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
}
.blog-page .blog-reading-band {
  width: 100%;
  background: ${TOKENS.blade2};
}
.blog-page .blog-reading-wrap {
  max-width: 1320px;
  margin: 0 auto;
  padding: 80px 32px 60px;
  box-sizing: border-box;
}
.blog-page .blog-intro-eyebrow-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}
.blog-page .blog-hairline {
  width: 24px;
  height: 1px;
  background: ${TOKENS.pinkM};
  flex-shrink: 0;
}
.blog-page .blog-ext-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}
.blog-page .blog-ext-card {
  display: flex;
  flex-direction: column;
  background: ${TOKENS.bg};
  border: 1px solid ${TOKENS.rule};
  border-radius: 8px;
  padding: 28px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}
.blog-page .blog-ext-card:hover {
  border-color: ${TOKENS.pinkM};
  transform: translateY(-3px);
  box-shadow: 0 12px 32px -16px rgba(114, 36, 62, 0.12);
}
.blog-page .blog-newsletter-band {
  width: 100%;
  background: linear-gradient(140deg, ${TOKENS.pinkL}, ${TOKENS.sageL});
  padding: 60px 0 80px;
  box-sizing: border-box;
}
.blog-page .blog-newsletter-shell {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;
}
.blog-page .blog-newsletter {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  align-items: center;
}
.blog-page .blog-news-form-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  border-bottom: 1px solid rgba(17, 17, 17, 0.18);
  padding-bottom: 12px;
}
.blog-page .blog-news-input--first,
.blog-page .blog-news-input--last {
  flex: 0 1 150px;
  min-width: 0;
}
.blog-page .blog-news-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 15px;
  color: ${TOKENS.text};
  outline: none;
  padding: 12px 0;
}
.blog-page .blog-news-btn {
  border: none;
  background: transparent;
  color: ${TOKENS.pinkD};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 12px 0;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
}
.blog-page .blog-news-msg {
  margin: 10px 0 0;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
  font-size: 13px;
  color: ${TOKENS.pinkD};
}
@media (max-width: 1100px) {
  .blog-page .blog-ext-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 980px) {
  .blog-page .blog-hero {
    grid-template-columns: 1fr;
    gap: 32px;
    max-height: none;
    padding: 48px 24px;
  }
  .blog-page .blog-hero__text-col {
    max-width: 100%;
    margin-left: 0;
    height: auto;
  }
  .blog-page .blog-hero__image-col {
    justify-content: center;
    height: auto;
  }
  .blog-page .blog-hero__image-wrapper {
    max-width: 360px;
    max-height: 420px;
  }
  .blog-page .blog-hero__image {
    max-height: 420px;
    object-fit: cover;
    object-position: 50% 22%;
  }
  .blog-page .blog-reading-wrap {
    padding: 56px 24px 48px;
  }
  .blog-page .blog-newsletter-band {
    padding: 48px 0 64px;
  }
  .blog-page .blog-newsletter-shell {
    padding: 0 24px;
  }
  .blog-page .blog-newsletter {
    grid-template-columns: 1fr;
  }
  .blog-page .blog-news-form-row {
    flex-wrap: wrap;
    align-items: center;
  }
  .blog-page .blog-news-input--first,
  .blog-page .blog-news-input--last {
    flex: 1 1 calc(50% - 8px);
  }
}
@media (max-width: 640px) {
  .blog-page .blog-ext-grid {
    grid-template-columns: 1fr;
  }
}
`;

function ExternalReadingCard({ card }) {
  return (
    <a
      className="blog-ext-card"
      href={card.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: TOKENS.pinkD,
          }}
        >
          {card.source}
        </span>
        <span style={{ color: TOKENS.body, fontSize: 14, lineHeight: 1 }} aria-hidden>
          ↗
        </span>
      </div>
      <h3
        className="serif"
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: TOKENS.text,
          letterSpacing: '-0.5px',
          margin: '0 0 6px',
          lineHeight: 1.25,
          flex: 1,
        }}
      >
        {card.title}
      </h3>
      <p
        className="reading-card__author"
        style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontStyle: 'italic',
          fontWeight: 400,
          fontSize: 14,
          color: TOKENS.body,
          margin: '0 0 14px 0',
          letterSpacing: 0,
        }}
      >
        {card.author}
      </p>
      <p
        style={{
          fontSize: 14,
          fontStyle: 'italic',
          color: TOKENS.body,
          margin: '0 0 18px',
          lineHeight: 1.65,
        }}
      >
        {card.why}
      </p>
      <div
        style={{
          borderTop: `1px solid ${TOKENS.rule}`,
          paddingTop: 16,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          fontSize: 12,
          color: TOKENS.body,
        }}
      >
        <span
          style={{
            background: TOKENS.pinkL,
            color: TOKENS.pinkD,
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {card.category}
        </span>
        <span aria-hidden style={{ fontSize: 8, lineHeight: 1, opacity: 0.6 }}>
          ●
        </span>
        <span>{card.meta}</span>
      </div>
    </a>
  );
}

export default function Blog() {
  const [nlBtn, setNlBtn] = useState('Subscribe →');
  const [nlSubmitting, setNlSubmitting] = useState(false);
  const [nlMessage, setNlMessage] = useState('');
  const nlFetchAbortRef = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Journal — OrivonAI';
    return () => {
      document.title = prev;
    };
  }, []);

  const onNewsletterSubmit = async (e) => {
    e.preventDefault();
    // Capture the form node before any `await`. React may clear
    // `e.currentTarget` on the synthetic event after the sync tick, so using
    // `e.currentTarget.reset()` after fetch throws and shows a false error.
    const form = e.currentTarget;
    const fd = new FormData(form);
    const firstName = String(fd.get('firstName') || '').trim();
    const lastName = String(fd.get('lastName') || '').trim();
    const email = String(fd.get('email') || '').trim().toLowerCase();

    if (!firstName || !lastName || !email) {
      setNlMessage('Please add your first name, last name, and email.');
      return;
    }

    nlFetchAbortRef.current?.abort();
    const ac = new AbortController();
    nlFetchAbortRef.current = ac;

    setNlSubmitting(true);
    setNlMessage('');
    try {
      const resp = await fetch(blogNewsletterSubscribeUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, source: 'blog_footer' }),
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: ac.signal,
      });
      const raw = await resp.text();
      let data = {};
      let parseOk = false;
      try {
        data = raw ? JSON.parse(raw) : {};
        parseOk = true;
      } catch {
        data = {};
        parseOk = false;
      }

      const okJson =
        parseOk &&
        (data?.ok === true ||
          data?.ok === 'true' ||
          /^\s*\{\s*"ok"\s*:\s*true\s*\}\s*$/.test(String(raw || '')));
      if (!resp.ok || !parseOk || !okJson) {
        const parts = [data?.error, data?.detail].filter(Boolean);
        if (!parseOk && raw) {
          parts.push(`(HTTP ${resp.status}, non-JSON response)`);
        } else if (!okJson && resp.ok) {
          parts.push(`(HTTP ${resp.status}, missing ok flag)`);
        } else if (!resp.ok) {
          parts.push(`(HTTP ${resp.status})`);
        }
        setNlMessage(parts.filter(Boolean).join(' — ') || 'Could not subscribe right now. Please try again.');
        setNlBtn('Subscribe →');
        return;
      }

      setNlBtn('Welcome ✦');
      setNlMessage("Thanks — you're on the list.");
      if (form && typeof form.reset === 'function') {
        form.reset();
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      console.error('[blog] newsletter subscribe', err);
      setNlBtn('Subscribe →');
      setNlMessage('Could not subscribe right now. Please try again.');
    } finally {
      if (nlFetchAbortRef.current === ac) {
        nlFetchAbortRef.current = null;
        setNlSubmitting(false);
      }
    }
  };

  return (
    <div className="blog-page">
      <style>{BLOG_PAGE_CSS}</style>
      <LandingNav positionMode="sticky" />

      <Link
        to="/blog/why-i-created-orivonai"
        aria-label="Read the founder story: Why I Created OrivonAI"
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <div className="blog-hero-band">
        <section className="blog-hero">
          <div className="blog-hero__text-col">
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: TOKENS.pinkD,
                  background: TOKENS.pinkL,
                  padding: '5px 10px',
                  borderRadius: 999,
                }}
              >
                New
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.24em',
                  textTransform: 'uppercase',
                  color: TOKENS.pinkD,
                }}
              >
                · Founder&apos;s story · Featured
              </span>
            </div>

            <h1
              className="serif"
              style={{
                fontSize: 'clamp(32px, 4vw, 44px)',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                color: TOKENS.text,
                lineHeight: 1.15,
                margin: '0 0 20px',
              }}
            >
              Why I Created{' '}
              <em style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic', color: TOKENS.pinkD }}>
                OrivonAI.
              </em>
            </h1>

            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                fontSize: 15,
                fontWeight: 400,
                lineHeight: 1.75,
                color: TOKENS.body,
                margin: '0 0 28px',
                maxWidth: 520,
              }}
            >
              Seven years. Six losses. Two children — and the 2 a.m. quiet that no app or friend group ever quite knew how to
              hold. The story behind the companion I wished I&apos;d had.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
              <img
                className="blog-hero__avatar"
                src="/images/blog/vanessa-profile.png"
                alt="Vanessa Mosbacker"
                width={48}
                height={48}
                loading="eager"
              />
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: TOKENS.body,
                  fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                }}
              >
                Vanessa Mosbacker · Founder &amp; CEO · 8 min read · April 29, 2026
              </p>
            </div>

            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: TOKENS.pinkD,
                borderBottom: `1.5px solid ${TOKENS.pink}`,
                paddingBottom: 4,
                display: 'inline-block',
                width: 'fit-content',
              }}
            >
              Read the story →
            </span>
          </div>

          <div className="blog-hero__image-col">
            <div className="blog-hero__image-wrapper">
              <img
                className="blog-hero__image"
                src="/images/blog/founder-amelie.png"
                alt="Vanessa Mosbacker, founder of OrivonAI, holding her daughter Amélie"
                loading="eager"
              />
              <span className="blog-hero__stamp">Chapter 01 · The origin</span>
            </div>
          </div>
        </section>
        </div>
      </Link>

      <div className="blog-reading-band">
      <section className="blog-reading-wrap">
        <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 48px' }}>
          <div className="blog-intro-eyebrow-row">
            <span className="blog-hairline" aria-hidden />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: TOKENS.pinkD,
              }}
            >
              Reading the conversation
            </span>
            <span className="blog-hairline" aria-hidden />
          </div>
          <h2
            className="serif"
            style={{
              fontSize: 'clamp(32px, 3.5vw, 44px)',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              color: TOKENS.text,
              margin: '0 0 16px',
              lineHeight: 1.2,
            }}
          >
            What we&apos;re{' '}
            <em style={{ color: TOKENS.pink, fontStyle: 'italic' }}>reading</em> while we write.
          </h2>
          <p
            style={{
              margin: '0 auto',
              maxWidth: 560,
              fontSize: 15,
              lineHeight: 1.75,
              color: TOKENS.body,
              fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
            }}
          >
            A handful of essays and books that have shaped how we think about every chapter. New journal entries from our team
            are coming weekly.
          </p>
        </div>

        <div className="blog-ext-grid">
          {READING_CARDS.map((card) => (
            <ExternalReadingCard key={card.href} card={card} />
          ))}
        </div>
      </section>
      </div>

      <div className="blog-newsletter-band">
      <div className="blog-newsletter-shell">
        <div className="blog-newsletter">
          <div>
            <h2
              className="serif"
              style={{
                fontSize: 'clamp(28px, 3vw, 42px)',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                color: TOKENS.text,
                margin: '0 0 16px',
                lineHeight: 1.2,
              }}
            >
              Be the first to read what&apos;s next.
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 15,
                lineHeight: 1.75,
                color: TOKENS.body,
                fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
              }}
            >
              Thoughtful letters from Vanessa and the OrivonAI team, delivered to your inbox.
            </p>
          </div>
          <form onSubmit={onNewsletterSubmit}>
            <div className="blog-news-form-row">
              <input
                className="blog-news-input blog-news-input--first"
                type="text"
                name="firstName"
                placeholder="First name"
                autoComplete="given-name"
              />
              <input
                className="blog-news-input blog-news-input--last"
                type="text"
                name="lastName"
                placeholder="Last name"
                autoComplete="family-name"
              />
              <input className="blog-news-input" type="email" name="email" placeholder="Email address" autoComplete="email" />
              <button className="blog-news-btn" type="submit" disabled={nlSubmitting} aria-busy={nlSubmitting}>
                {nlSubmitting ? 'Submitting…' : nlBtn}
              </button>
            </div>
            {nlMessage ? <p className="blog-news-msg">{nlMessage}</p> : null}
          </form>
        </div>
      </div>
      </div>

      <SiteFooter />
    </div>
  );
}
